/**
 * Auto Raise Delivery Appeal section.
 *
 * CR-7068: When enabled, the system automatically raises delivery appeals for
 * late/early GRNs where the supplier's promise date differs from the original
 * delivery date. This matches the legacy OrderbookService behavior.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Loader2, Bell } from 'lucide-react';
import { platformSettingsAPI } from '../../../lib/api';
import { toast } from 'sonner';

export function AutoRaiseDeliveryAppealSection() {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await platformSettingsAPI.getSettings();
        setEnabled(!!data.auto_raise_delivery_appeals);
      } catch (err: unknown) {
        console.error('Error loading auto-raise setting:', err);
        toast.error('Failed to load Auto Raise Delivery Appeal setting');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleToggle = async (checked: boolean) => {
    const previous = enabled;
    setEnabled(checked);
    try {
      await platformSettingsAPI.patchSettings({ auto_raise_delivery_appeals: checked });
      toast.success(`Auto Raise Delivery Appeals: ${checked ? 'ON' : 'OFF'}`);
    } catch (err: unknown) {
      console.error('Error saving auto-raise setting:', err);
      toast.error('Failed to save Auto Raise Delivery Appeal setting');
      setEnabled(previous);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Auto Raise Delivery Appeals
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          When enabled, the system automatically raises delivery appeals for late or early
          deliveries where the supplier&apos;s promise date differs from the original delivery date.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between rounded-md border border-input bg-background px-4 py-3">
          <div className="space-y-1 pr-3">
            <Label htmlFor="auto-raise-delivery-appeal" className="text-sm font-medium">
              Auto Raise Delivery Appeals
            </Label>
            <p className="text-xs text-muted-foreground">
              {enabled
                ? 'ON — Appeals are automatically raised for qualifying late/early deliveries.'
                : 'OFF — Suppliers must manually raise delivery appeals.'}
            </p>
          </div>
          <Switch
            id="auto-raise-delivery-appeal"
            checked={enabled}
            onCheckedChange={handleToggle}
          />
        </div>
      </CardContent>
    </Card>
  );
}
